/* Fiche contact (panneau latéral) : création, modification et actions. */
import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import {
  AlertCircle, Archive, ArchiveRestore, ArrowRight, Building2, Check, Download, FileText, Loader2, Plus, Save,
  Send, Trash2, Upload, User, UserPlus, Users, X,
} from 'lucide-react';
import type { AccountState, CrmContact, CrmContactDraft, Directory, InstallationRight, Meter, Role, SharingOperation, Stage, VatRegime } from './types';
import { INSTALLATION_RIGHT_LABELS, isSepaEligible, mandateState, PERIOD_LABELS, type ReimbursementPeriod, ROLE_LABELS, SIBELGA_STATUS_LABELS, type SibelgaStatus, STAGES, STAGE_INDEX, VAT_LABELS, WAITING_LABELS, type WaitingOn, displayName, emptyMeter, templateVarsFor, TEMPLATE_VARIABLES, formatBce, isGroupCard as isGroupCardFn, signatoryName, DEFAULT_ANNUAL_FEE, defaultAnnualFee, feeAfterEntityChange } from './types';
import { type DefaultPrices, defaultPricesFor, fetchDefaultPrices, pricesAfterChange } from './defaultPrices';
import { type FieldKey, fieldLabel, formatErrors, isEmpty, isImported, missingFields, nextStage, normalizeDraft, requiredFieldsFor } from './validation';
import {
  type GroupPrice, archiveContact, createContact, createParticipantsForContact, downloadBase64Pdf, fetchGroupPrice, generateContract,
  openStoredFile, targetGroupFor, sendQuestionnaire, updateContact, uploadSignedContract,
} from './api';
import { Field, SuggestInput, borderFor, formatDateFr, inputClass, errorMessage } from './ui';
import { PlatformSection } from './PlatformSection';
import { MandateSection } from './MandateSection';
import { type SavingsEstimateSettings, computeSavings, fetchSavingsSettings, formatEuros, formatPrice as formatKwhPrice } from './savingsEstimate';
import { bicFromBelgianIban } from '../../lib/belgianBanks';
import {
  BILLING_CHANNELS, BILLING_CHANNEL_LABELS, type BillingChannel, resolveBillingChannel,
} from '../../../supabase/functions/_shared/crmTodo';
import { SibelgaStatusPicker } from './SibelgaStatus';
import {
  VAT_RATE_OPTIONS, applyParticipantPatches, contactFromParticipants, fetchParticipantsByIds, hasVariablePrice,
  participantPatchErrors, participantPatches,
} from './participants';

interface Props {
  /** null = création d'un nouveau lead */
  contact: CrmContact | null;
  operations: SharingOperation[];
  account?: AccountState;
  /** Groupes et sociétés existants, pour les suggestions de saisie. */
  directory: Directory;
  /** Étape visée : ses champs manquants sont surlignés. */
  highlightStage?: Stage | null;
  onClose: () => void;
  onSaved: (c: CrmContact) => void;
  onRequestMove: (c: CrmContact, target: Stage) => void;
}

const NUMERIC_FIELDS = new Set<FieldKey>([
  'price_cents_kwh', 'sell_price_cents_kwh', 'annual_fee', 'sharing_price_cents_kwh', 'sharing_sell_price_cents_kwh', 'installed_power_kwc',
]);

const comparable = (d: CrmContactDraft) => JSON.stringify(normalizeDraft(d));
const isProsumerRole = (r: Role | null | undefined) => r === 'prosumer';
const formatPrice = (v: number) => v.toLocaleString('fr-BE', { maximumFractionDigits: 4 });

export function ContactDrawer({ contact, operations, account, directory, highlightStage, onClose, onSaved, onRequestMove }: Props) {
  const isNew = contact === null;
  const [draft, setDraft] = useState<CrmContactDraft>(() =>
    contact ? { ...contact, meters: contact.meters.map(m => ({ ...m })) } : { entity_type: 'physique', role: null, source: 'manuel', meters: [emptyMeter()], annual_fee: DEFAULT_ANNUAL_FEE.physique },
  );
  const [saved, setSaved] = useState<CrmContactDraft>(() => (contact ? { ...contact } : {}));
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  /* Tarifs par défaut (Paramètres) : proposés aux leads selon le type de membre et de personne. */
  const [defaultPrices, setDefaultPrices] = useState<DefaultPrices | null>(null);
  useEffect(() => { fetchDefaultPrices().then(setDefaultPrices).catch(() => setDefaultPrices(null)); }, []);

  const stage: Stage = contact?.stage ?? 'lead';
  /** Membre repris de la base existante : aucun champ imposé (e-mail compris). */
  const imported = isImported(contact);

  /*
   * Membre actif (hors carte de groupe) : ses fiches participants font foi, comme dans le formulaire de la page admin.
   * À l'ouverture, la fiche CRM reprend leurs valeurs ; à l'enregistrement, les champs modifiés y sont écrits.
   */
  const syncWithParticipants = !!contact && contact.stage === 'active' && !contact.groupe && !contact.merged_into_id && contact.meters.some(m => m.participant_id);
  const [variablePrice, setVariablePrice] = useState(false);
  const [participantsLoaded, setParticipantsLoaded] = useState(!syncWithParticipants);
  useEffect(() => {
    if (!syncWithParticipants || !contact) return;
    let cancelled = false;
    fetchParticipantsByIds(contact.meters.map(m => m.participant_id ?? ''))
      .then(rows => {
        if (cancelled) return;
        setVariablePrice(hasVariablePrice(rows));
        const patch = contactFromParticipants(contact, rows) as Record<string, unknown>;
        const original = contact as unknown as Record<string, unknown>;
        // N'écrase pas ce que l'utilisateur a déjà commencé à modifier.
        setDraft(prev => {
          const next: Record<string, unknown> = { ...prev };
          for (const [k, v] of Object.entries(patch)) {
            if (JSON.stringify((prev as Record<string, unknown>)[k] ?? null) === JSON.stringify(original[k] ?? null)) next[k] = v;
          }
          return next as CrmContactDraft;
        });
        setSaved(prev => ({ ...prev, ...patch }));
      })
      .catch(err => toast.error(`Fiches participants : ${errorMessage(err)}`))
      .finally(() => { if (!cancelled) setParticipantsLoaded(true); });
    return () => { cancelled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const target: Stage | null = isNew ? 'lead' : (highlightStage ?? nextStage(stage));
  const forward = !isNew && target !== null && STAGE_INDEX[target] > STAGE_INDEX[stage];

  const missingList = useMemo(
    () => (target ? missingFields(draft, target, { excludeDialogFields: forward }) : []),
    [draft, target, forward],
  );
  const missing = useMemo(() => new Set(missingList.map(m => m.key)), [missingList]);
  const required = useMemo(
    () => (imported ? new Set<FieldKey>() : new Set(requiredFieldsFor(draft, target && forward ? target : stage))),
    [draft, target, forward, stage, imported],
  );
  const errors = formatErrors(draft);
  const dirty = isNew || comparable(draft) !== comparable(saved);

  const knownCompanyName = directory.companyNumberToName[formatBce(draft.company_number) ?? ''];
  const companyMismatch = !!knownCompanyName && !!draft.company_name?.trim()
    && draft.company_name.trim().toLowerCase() !== knownCompanyName.toLowerCase();
  useEffect(() => {
    if (knownCompanyName && !draft.company_name?.trim()) setDraft(prev => ({ ...prev, company_name: knownCompanyName }));
  }, [knownCompanyName]); // eslint-disable-line react-hooks/exhaustive-deps

  const isGroupCard = isGroupCardFn(contact);
  const plannedGroup = targetGroupFor({ ...(draft as CrmContact), meters: draft.meters ?? [] });
  const existingGroup = plannedGroup ? directory.groups.find(g => g.name.toLowerCase() === plannedGroup.toLowerCase()) : undefined;

  /* Prix du groupe visé : proposé d'office à une société qui rejoint un groupe existant (tant que le contrat n'est pas envoyé). */
  const [groupPrice, setGroupPrice] = useState<GroupPrice | null>(null);
  const groupKey = !isGroupCard && existingGroup ? existingGroup.name : null;
  const priceEditable = isNew || stage === 'lead';
  useEffect(() => {
    setGroupPrice(null);
    if (!groupKey) return;
    let cancelled = false;
    Promise.all([fetchGroupPrice(groupKey, draft.role ?? null, contact?.id ?? null), fetchDefaultPrices().catch(() => null)])
      .then(([gp, defaults]) => {
        if (cancelled) return;
        setGroupPrice(gp);
        if (gp && priceEditable) {
          setDraft(prev => {
            // Vide ou encore au tarif par défaut : le prix du groupe prime.
            const def = defaultPricesFor(defaults, prev.entity_type, prev.role);
            const fillBuy = (isEmpty(prev.price_cents_kwh) || Number(prev.price_cents_kwh) === def.price) && gp.price !== null;
            const fillSell = prev.role === 'prosumer' && (isEmpty(prev.sell_price_cents_kwh) || Number(prev.sell_price_cents_kwh) === def.sell) && gp.sellPrice !== null;
            if (!fillBuy && !fillSell) return prev;
            return {
              ...prev,
              ...(fillBuy ? { price_cents_kwh: gp.price } : {}),
              ...(fillSell ? { sell_price_cents_kwh: gp.sellPrice } : {}),
            };
          });
        }
      })
      .catch(() => { if (!cancelled) setGroupPrice(null); });
    return () => { cancelled = true; };
  }, [groupKey, draft.role]); // eslint-disable-line react-hooks/exhaustive-deps
  const groupPriceDiffers = !!groupPrice && (
    (groupPrice.price !== null && Number(draft.price_cents_kwh) !== groupPrice.price)
    || (isProsumerRole(draft.role) && groupPrice.sellPrice !== null && Number(draft.sell_price_cents_kwh) !== groupPrice.sellPrice)
  );
  const applyGroupPrice = () => groupPrice && setDraft(prev => ({
    ...prev,
    ...(groupPrice.price !== null ? { price_cents_kwh: groupPrice.price } : {}),
    ...(prev.role === 'prosumer' && groupPrice.sellPrice !== null ? { sell_price_cents_kwh: groupPrice.sellPrice } : {}),
  }));

  const isPM = draft.entity_type === 'morale';
  const producerLike = draft.role === 'producer' || draft.role === 'prosumer';
  /* Domiciliation SEPA : particulier consommateur. Mandat actif : compte verrouillé (révoquer puis nouveau mandat). */
  const sepaEligible = isSepaEligible(draft);
  const accountLocked = sepaEligible && mandateState(saved) === 'active';
  /** IBAN saisi : BIC proposé pour une banque belge (s'il est vide ou avait été proposé automatiquement). */
  const setIban = (value: string) => setDraft(prev => {
    const next: CrmContactDraft = { ...prev, iban: value === '' ? null : value };
    const before = bicFromBelgianIban(prev.iban ?? '');
    const proposed = bicFromBelgianIban(value);
    if (proposed && (!prev.bic || prev.bic === before)) next.bic = proposed;
    return next;
  });
  /* Facturation (consommateurs, prosumers) : canal choisi dans la fiche, sinon canal automatique. */
  const billed = draft.role === 'consumer' || draft.role === 'prosumer' || !!draft.billing_channel;
  const autoChannel = resolveBillingChannel(
    { billing_channel: null, role: draft.role ?? null, entity_type: draft.entity_type ?? null, mandate_status: draft.mandate_status ?? null },
    { companyNumber: draft.company_number ?? null },
  );
  const effectiveChannel: BillingChannel = draft.billing_channel ?? autoChannel;
  const directDebitBlock = draft.role !== 'consumer' ? 'consommateurs uniquement'
    : draft.entity_type !== 'physique' ? 'particuliers uniquement'
      : draft.mandate_status !== 'active' ? 'mandat SEPA actif requis' : null;
  const CHANNEL_NOTES: Record<BillingChannel, string> = {
    peppol: 'PDF envoyé à Accountable (revenus).',
    email: 'PDF envoyé à Accountable (revenus) et au membre par e-mail.',
    direct_debit: 'PDF à Accountable, pré-notification au membre avec la date de prélèvement, puis fichier XML pour la banque.',
  };
  /* Estimation des économies (particulier consommateur ou prosumer), envoyée avec le contrat. */
  const estimateRelevant = (draft.entity_type === 'physique' && (draft.role === 'consumer' || draft.role === 'prosumer'))
    || !!draft.estimate_requested || draft.annual_consumption_kwh != null;
  const [savingsSettings, setSavingsSettings] = useState<SavingsEstimateSettings | null>(null);
  useEffect(() => {
    if (!estimateRelevant || savingsSettings) return;
    fetchSavingsSettings().then(setSavingsSettings).catch(() => setSavingsSettings(null));
  }, [estimateRelevant]); // eslint-disable-line react-hooks/exhaustive-deps
  const savingsPreview = savingsSettings
    ? computeSavings({ annual_consumption_kwh: draft.annual_consumption_kwh ?? null, annual_cost_eur: draft.annual_cost_eur ?? null }, savingsSettings)
    : null;
  const setNumber = (key: FieldKey, raw: string) => {
    const v = raw.replace(/\s/g, '').replace(',', '.');
    set(key, v === '' ? null : Number(v));
  };
  const isProsumer = draft.role === 'prosumer';
  const meters = draft.meters ?? [];
  const label = (key: FieldKey) => fieldLabel(key, draft.entity_type, draft.role);
  const signatoryHint = signatoryName({
    entity_type: draft.entity_type ?? null, company_name: draft.company_name ?? null,
    first_name: draft.first_name ?? null, last_name: draft.last_name ?? null, email: null,
  }).replace(/^Sans nom$/, 'Société');

  const set = (key: FieldKey, value: unknown) => setDraft(prev => ({ ...prev, [key]: value }));
  /** Lead : les tarifs vides ou au tarif par défaut suivent le type de membre / de personne. */
  const withDefaultPrices = (prev: CrmContactDraft, next: CrmContactDraft): CrmContactDraft =>
    priceEditable ? { ...next, ...pricesAfterChange(defaultPrices, prev, next) } : next;
  const suggested = defaultPricesFor(defaultPrices, draft.entity_type, draft.role);
  const valueOf = (key: FieldKey): string => {
    const v = (draft as Record<string, unknown>)[key];
    if (v === null || v === undefined) return '';
    return typeof v === 'string' && /(_at|_date)$/.test(key) ? v.slice(0, 10) : String(v);
  };

  /** Variables des modèles d'e-mail remplies par un champ (affichées à côté de son libellé). */
  const varsOf = (key: FieldKey) => templateVarsFor(key, draft.entity_type).map(v => ({ key: v, label: TEMPLATE_VARIABLES.find(t => t.key === v)?.label ?? v }));
  const input = (key: FieldKey, opts: { type?: string; placeholder?: string; full?: boolean } = {}) => (
    <Field label={label(key)} vars={varsOf(key)} required={required.has(key)} missing={missing.has(key)} error={errors[key]} className={opts.full ? 'col-span-2' : ''}>
      <input
        type={opts.type ?? 'text'}
        step={opts.type === 'number' ? 'any' : undefined}
        value={valueOf(key)}
        placeholder={opts.placeholder}
        onChange={e => {
          const raw = e.target.value;
          set(key, raw === '' ? null : NUMERIC_FIELDS.has(key) ? Number(raw) : raw);
        }}
        className={`${inputClass} ${borderFor(missing.has(key), errors[key])}`}
      />
    </Field>
  );

  const setMeter = (i: number, patch: Partial<Meter>) =>
    setDraft(prev => ({ ...prev, meters: (prev.meters ?? []).map((m, idx) => (idx === i ? { ...m, ...patch } : m)) }));
  const addMeter = () => setDraft(prev => ({ ...prev, meters: [...(prev.meters ?? []), emptyMeter()] }));
  const removeMeter = (i: number) => {
    const m = meters[i];
    if (m.participant_id) {
      toast.error('Ce compteur est lié à une fiche participant : supprimez-la depuis le tableau de bord de l’opération');
      return;
    }
    setDraft(prev => ({ ...prev, meters: (prev.meters ?? []).filter((_, idx) => idx !== i) }));
  };

  /* ── Enregistrement ── */
  const save = async (): Promise<CrmContact | null> => {
    if (Object.keys(errors).length > 0) {
      toast.error('Corrigez les champs en rouge avant d’enregistrer');
      return null;
    }
    if (isNew) {
      const leadMissing = missingFields(draft, 'lead');
      if (leadMissing.length > 0) {
        toast.error(`Champs obligatoires : ${leadMissing.map(m => m.label).join(', ')}`);
        return null;
      }
    } else if (!imported) {
      const stageMissing = missingFields(draft, stage);
      if (stageMissing.length > 0) {
        toast.error(`Champs obligatoires à cette étape : ${stageMissing.map(m => m.label).join(', ')}`);
        return null;
      }
    }
    const clean = normalizeDraft({ ...draft, meters: meters.filter(m => !isEmpty(m.ean_code) || !isEmpty(m.meter_address) || m.participant_id) });
    const patches = syncWithParticipants && participantsLoaded
      ? participantPatches(normalizeDraft(saved), clean, variablePrice)
      : new Map<string, Record<string, unknown>>();
    const patchErrors = participantPatchErrors(patches, clean.role);
    if (patchErrors.length > 0) {
      toast.error(patchErrors.join(' · '));
      return null;
    }
    setSaving(true);
    try {
      // Fiches participants d'abord (elles font foi), puis la fiche CRM.
      const updatedParticipants = await applyParticipantPatches(patches);
      const result = isNew ? await createContact(clean) : await updateContact(contact!.id, clean);
      setDraft({ ...result, meters: result.meters.length ? result.meters : [emptyMeter()] });
      setSaved({ ...result, meters: result.meters.length ? result.meters : [emptyMeter()] });
      onSaved(result);
      toast.success(isNew ? 'Lead créé' : updatedParticipants > 0
        ? `Fiche enregistrée · ${updatedParticipants} fiche${updatedParticipants > 1 ? 's' : ''} participant mise${updatedParticipants > 1 ? 's' : ''} à jour`
        : 'Fiche enregistrée');
      return result;
    } catch (err) {
      toast.error(`Erreur : ${errorMessage(err)}`);
      return null;
    } finally {
      setSaving(false);
    }
  };

  /* ── Actions ── */
  const current = (): CrmContact => ({ ...(contact as CrmContact), ...draft, meters } as CrmContact);

  const run = async (key: string, fn: () => Promise<void>) => {
    setBusy(key);
    try {
      await fn();
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy(null);
    }
  };

  const patchAndNotify = async (patch: CrmContactDraft, message: string) => {
    const result = await updateContact(contact!.id, patch);
    setDraft(prev => ({ ...prev, ...patch }));
    setSaved(prev => ({ ...prev, ...patch }));
    onSaved(result);
    toast.success(message);
  };

  const requireSaved = () => {
    if (dirty) throw new Error('Enregistrez d’abord la fiche');
  };

  const goNext = async () => {
    const next = nextStage(stage);
    if (!next || !contact) return;
    const base = dirty ? await save() : contact;
    if (base) onRequestMove(base, next);
  };

  const handlePreview = () => run('preview', async () => {
    requireSaved();
    const { pdfBase64, fileName, warnings } = await generateContract(contact!.id);
    downloadBase64Pdf(pdfBase64, fileName);
    warnings?.forEach(w => toast(w, { duration: 8000 }));
  });

  const handleQuestionnaire = () => run('questionnaire', async () => {
    requireSaved();
    if (draft.questionnaire_sent_at && !window.confirm(`Le formulaire a déjà été envoyé le ${formatDateFr(draft.questionnaire_sent_at)}. Le renvoyer ?`)) return;
    const result = await sendQuestionnaire(current(), operations);
    setDraft(prev => ({ ...prev, questionnaire_sent_at: result.questionnaire_sent_at }));
    setSaved(prev => ({ ...prev, questionnaire_sent_at: result.questionnaire_sent_at }));
    onSaved(result);
    toast.success(`Formulaire envoyé à ${result.email}`);
  });

  /** Statut Sibelga : enregistré tout de suite. */
  const setSibelgaStatus = (next: SibelgaStatus[]) => run('sibelga-status', async () => {
    await patchAndNotify({ sibelga_status: next }, next.length
      ? `Statut Sibelga : ${next.map(s => SIBELGA_STATUS_LABELS[s].toLowerCase()).join(', ')}`
      : 'Statut Sibelga retiré');
  });

  /** Statut d'attente : enregistré tout de suite (la précision s'enregistre avec la fiche). */
  const setWaiting = (w: WaitingOn | null) => run('waiting', async () => {
    const now = new Date().toISOString();
    await patchAndNotify(
      { waiting_on: w, waiting_since: w ? now : null, ...(w ? {} : { waiting_note: null }) },
      w ? `Statut : ${WAITING_LABELS[w].toLowerCase()}` : 'Statut retiré',
    );
  });

  const handleSignedUpload = (file: File) => run('signed', async () => {
    const path = await uploadSignedContract(contact!.id, file);
    await patchAndNotify({ signed_contract_path: path }, 'Contrat signé ajouté');
  });

  const handleCreateParticipants = () => run('participants', async () => {
    requireSaved();
    const updated = await createParticipantsForContact(current(), current().groupe ?? targetGroupFor(current()));
    await patchAndNotify({ meters: updated }, 'Fiches participants créées');
  });

  const handleArchive = () => run('archive', async () => {
    const archived = !draft.archived_at;
    if (archived && !window.confirm('Archiver ce contact ? Il disparaît du tableau (visible avec le filtre « Archivés »).')) return;
    const result = await archiveContact(contact!.id, archived);
    onSaved(result);
    toast.success(archived ? 'Contact archivé' : 'Contact restauré');
    onClose();
  });

  const next = isNew ? null : nextStage(stage);
  const stageMeta = STAGES.find(s => s.key === stage)!;
  const operationName = operations.find(o => o.id === draft.sharing_operation_id)?.name;
  const metersWithoutParticipant = meters.filter(m => !m.participant_id && !isEmpty(m.ean_code)).length;

  const busyIcon = (key: string, Icon: React.ElementType) =>
    busy === key ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Icon className="w-3.5 h-3.5" />;
  const secondaryBtn = 'flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-neutral-200 text-xs text-neutral-700 hover:bg-neutral-50 disabled:opacity-50';
  const segBtn = (active: boolean, warn = false) =>
    `flex items-center justify-center gap-2 py-2 rounded-lg text-xs font-semibold border transition-colors ${
      active ? 'border-[#F8BD3D] bg-amber-50 text-neutral-900' : `text-neutral-500 hover:border-neutral-300 ${warn ? 'border-amber-400' : 'border-neutral-200'}`
    }`;
  const sectionTitle = (t: string, extra?: React.ReactNode) => (
    <div className="flex items-center justify-between">
      <h4 className="text-xs font-semibold text-neutral-500 uppercase tracking-wide">{t}</h4>
      {extra}
    </div>
  );

  return (
    <div className="fixed inset-0 z-[60] flex justify-end">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative w-full max-w-xl h-full bg-white shadow-2xl flex flex-col">
        {/* En-tête */}
        <div className="px-6 py-4 border-b border-neutral-100 shrink-0">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <h3 className="text-base font-semibold text-neutral-900 truncate">{isNew ? 'Nouveau lead' : displayName(current())}</h3>
              {!isNew && (
                <p className="flex items-center gap-1.5 text-xs text-neutral-500 mt-0.5">
                  <span className={`w-2 h-2 rounded-full ${stageMeta.dot}`} />
                  {stageMeta.label}
                  {contact?.source === 'formulaire' && <span>· via formulaire</span>}
                  {operationName && <span>· {operationName}</span>}
                  {draft.archived_at && <span className="text-red-600 font-medium">· archivé</span>}
                </p>
              )}
            </div>
            <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-neutral-100 text-neutral-400" aria-label="Fermer">
              <X className="w-4 h-4" />
            </button>
          </div>
          {!isNew && (
            <div className="flex items-center gap-1 mt-3">
              {STAGES.map(s => (
                <div key={s.key} title={s.label} className={`h-1.5 flex-1 rounded-full ${STAGE_INDEX[s.key] <= STAGE_INDEX[stage] ? s.dot : 'bg-neutral-200'}`} />
              ))}
            </div>
          )}
          {!isNew && stage !== 'active' && (
            <div className="mt-3 space-y-2">
              <div className="flex flex-wrap items-center gap-1.5">
                <span className="text-[11px] text-neutral-500 mr-0.5">Statut :</span>
                {(['client', 'sunisup'] as WaitingOn[]).map(w => (
                  <button key={w} type="button" onClick={() => setWaiting(draft.waiting_on === w ? null : w)} disabled={!!busy}
                    className={`px-2 py-1 rounded-full text-[11px] font-semibold border transition-colors ${draft.waiting_on === w
                      ? (w === 'sunisup' ? 'border-amber-400 bg-amber-50 text-amber-800' : 'border-neutral-400 bg-neutral-100 text-neutral-800')
                      : 'border-neutral-200 text-neutral-500 hover:border-neutral-300'}`}>
                    {WAITING_LABELS[w]}
                  </button>
                ))}
                {draft.waiting_on && draft.waiting_since && <span className="text-[10px] text-neutral-400">depuis le {formatDateFr(draft.waiting_since)}</span>}
              </div>
              {draft.waiting_on && (
                <input value={draft.waiting_note ?? ''} onChange={e => set('waiting_note', e.target.value || null)}
                  placeholder={draft.waiting_on === 'sunisup' ? 'Ce qu’il reste à faire (facultatif), ex. préparer le contrat' : 'Ce qu’on attend (facultatif), ex. facture d’électricité'}
                  className={`${inputClass} py-1.5 text-xs border-neutral-200`} />
              )}
              {draft.waiting_on === 'sunisup' && <p className="text-[10px] text-neutral-400">Ce contact apparaît dans Communications → À faire et dans le récap mensuel.</p>}
            </div>
          )}
          {missingList.length > 0 && target && (
            <div className="flex items-start gap-2 mt-3 p-2.5 rounded-lg bg-amber-50 border border-amber-200 text-[11px] text-amber-800">
              <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
              <span>
                {isNew ? 'Pour créer le lead' : `Pour passer à « ${STAGES.find(s => s.key === target)?.label} »`}, complétez : {missingList.map(m => m.label).join(', ')}.
              </span>
            </div>
          )}
        </div>

        {/* Corps */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
          <section className="space-y-3">
            {sectionTitle('Profil')}
            <div className="grid grid-cols-2 gap-2">
              {(['physique', 'morale'] as const).map(e => (
                <button key={e} type="button" onClick={() => setDraft(prev => withDefaultPrices(prev, { ...prev, entity_type: e, annual_fee: feeAfterEntityChange(prev.annual_fee, prev.entity_type, e) }))} className={segBtn(draft.entity_type === e, missing.has('entity_type'))}>
                  {e === 'physique' ? <User className="w-3.5 h-3.5" /> : <Building2 className="w-3.5 h-3.5" />}
                  {e === 'physique' ? 'Personne physique' : 'Personne morale'}
                </button>
              ))}
            </div>
            <div className="grid grid-cols-3 gap-2">
              {(Object.keys(ROLE_LABELS) as Role[]).map(r => (
                <button key={r} type="button" onClick={() => setDraft(prev => withDefaultPrices(prev, { ...prev, role: r }))} className={segBtn(draft.role === r, missing.has('role'))}>
                  {ROLE_LABELS[r]}
                </button>
              ))}
            </div>
            <Field label="Groupe (facultatif)">
              <SuggestInput
                value={draft.groupe ?? ''}
                onChange={v => set('groupe', v.trim() === '' ? null : v)}
                suggestions={directory.groups.map(g => ({ value: g.name, hint: g.eanCount ? `${g.eanCount} EAN` : undefined }))}
                placeholder="Ex. Compagnie ABC, Sun4School…"
                disabled={isGroupCard}
                icon={<Users className="w-3.5 h-3.5 text-amber-600" />}
                className="border-neutral-200"
              />
            </Field>
            <p className="text-[11px] text-neutral-500 -mt-1">
              {isGroupCard
                ? 'Carte du groupe. Le nom du groupe se modifie dans le tableau de bord de l’opération.'
                : plannedGroup && existingGroup
                  ? `Au passage en « Membre actif », les EAN de ce membre rejoindront le groupe existant « ${existingGroup.name} »${existingGroup.eanCount ? ` (${existingGroup.eanCount} EAN)` : ''}.`
                  : plannedGroup
                    ? `Au passage en « Membre actif », un nouveau groupe « ${plannedGroup} » sera créé.`
                    : 'Un membre avec un seul EAN et sans groupe reste un membre individuel.'}
            </p>
          </section>

          <section className="space-y-3">
            {sectionTitle('Identité')}
            <div className="grid grid-cols-2 gap-3">
              {isPM ? (
                <>
                  <Field label={label('company_name')} vars={varsOf('company_name')} required={required.has('company_name')} missing={missing.has('company_name')} className="col-span-2">
                    <SuggestInput
                      value={draft.company_name ?? ''}
                      onChange={v => set('company_name', v === '' ? null : v)}
                      suggestions={directory.companyNames.map(n => ({ value: n }))}
                      placeholder="Dénomination officielle"
                      className={companyMismatch ? 'border-amber-400' : borderFor(missing.has('company_name'))}
                    />
                    {companyMismatch && (
                      <span className="block text-[11px] text-amber-700 mt-1">
                        Ce numéro d’entreprise correspond déjà à « {knownCompanyName} ». Utilisez le même nom pour éviter une erreur de regroupement dans les factures.
                      </span>
                    )}
                  </Field>
                  {input('legal_form', { placeholder: 'SRL, SA, ASBL…' })}
                  {input('company_number', { placeholder: 'BE 0123.456.789' })}
                  {input('first_name')}
                  {input('last_name')}
                  {input('representative_title', { placeholder: 'Gérant, administrateur…', full: true })}
                </>
              ) : (
                <>
                  {input('first_name')}
                  {input('last_name')}
                  {input('national_register', { placeholder: '11 chiffres' })}
                  {input('birth_date', { type: 'date' })}
                </>
              )}
            </div>
          </section>

          <section className="space-y-3">
            {sectionTitle('Coordonnées')}
            <div className="grid grid-cols-2 gap-3">
              {input('email', { type: 'email' })}
              {input('phone', { type: 'tel', placeholder: '+32 …' })}
              {input('domicile_address', { full: true })}
              {input('billing_email', { type: 'email', placeholder: 'Si différent' })}
              {input('billing_address', { placeholder: 'Si différente' })}
            </div>
            {isGroupCard && (
              <p className="text-[11px] text-neutral-500">
                Contact unique du groupe : il reçoit l’invitation et le message de bienvenue. Les e-mails de facturation de chaque EAN se gèrent dans le tableau de bord de l’opération.
              </p>
            )}
          </section>

          <section className="space-y-2">
            {sectionTitle(`Points de comptage (${meters.length} EAN · un seul contrat)`, (
              <button type="button" onClick={addMeter} className="flex items-center gap-1 text-xs font-medium text-neutral-700 hover:text-neutral-900">
                <Plus className="w-3.5 h-3.5" />Ajouter un compteur
              </button>
            ))}
            <p className="text-[11px] text-neutral-500">
              Variables : <code className="px-1 py-px rounded bg-neutral-100 text-[10px] font-mono text-neutral-500" title="Premier code EAN">{'{{ean}}'}</code> premier EAN,{' '}
              <code className="px-1 py-px rounded bg-neutral-100 text-[10px] font-mono text-neutral-500" title="Tous les codes EAN">{'{{liste_ean}}'}</code> tous les EAN
            </p>
            {missing.has('meters') && <p className="text-[11px] text-amber-700">Ajoutez au moins un point de comptage.</p>}
            {meters.map((m, i) => {
              const eanKey = `meters.${i}.ean_code`;
              const addrKey = `meters.${i}.meter_address`;
              const nameKey = `meters.${i}.name`;
              const rowWarn = missing.has(eanKey) || missing.has(addrKey) || missing.has(nameKey);
              return (
                <div key={i} className={`p-2.5 rounded-lg border space-y-1.5 ${rowWarn ? 'border-amber-300 bg-amber-50/50' : 'border-neutral-200'}`}>
                  <div className="grid grid-cols-[20px_minmax(0,1fr)_auto] items-center gap-2">
                    <span className="w-5 h-5 rounded-full bg-neutral-100 text-[10px] font-semibold text-neutral-500 flex items-center justify-center">{i + 1}</span>
                    <input
                      value={m.name ?? ''}
                      onChange={e => setMeter(i, { name: e.target.value || null })}
                      placeholder={`Nom du participant${required.has('meter_names') ? ' *' : ''} (ex. ${signatoryHint} ${i + 1})`}
                      aria-label={`Nom du participant du compteur ${i + 1}`}
                      className={`${inputClass} font-medium ${borderFor(missing.has(nameKey))}`}
                    />
                    {m.participant_id
                      ? <span className="flex items-center gap-1 text-[10px] text-emerald-700 whitespace-nowrap"><Check className="w-3.5 h-3.5" />Participant</span>
                      : <button type="button" onClick={() => removeMeter(i)} className="p-1.5 text-neutral-400 hover:text-red-600" aria-label="Supprimer le compteur"><Trash2 className="w-3.5 h-3.5" /></button>}
                  </div>
                  <div className="grid grid-cols-[minmax(0,11.5rem)_minmax(0,1fr)] items-center gap-2 pl-7">
                    <input
                      value={m.ean_code}
                      onChange={e => setMeter(i, { ean_code: e.target.value })}
                      placeholder="54xxxxxxxxxxxxxxxx"
                      disabled={!!m.participant_id}
                      title={m.participant_id ? 'EAN lié à une fiche participant : non modifiable ici' : undefined}
                      className={`${inputClass} font-mono text-[13px] ${borderFor(missing.has(eanKey), errors[eanKey])}`}
                    />
                    <input
                      value={m.meter_address ?? ''}
                      onChange={e => setMeter(i, { meter_address: e.target.value || null })}
                      placeholder="Adresse du compteur"
                      className={`${inputClass} ${borderFor(missing.has(addrKey))}`}
                    />
                  </div>
                  <div className="flex items-center gap-3 pl-7">
                    {(['intelligent', 'mecanique'] as const).map(t => (
                      <label key={t} className="flex items-center gap-1 text-[11px] text-neutral-600 cursor-pointer">
                        <input type="radio" name={`meter-type-${contact?.id ?? 'new'}-${i}`} checked={m.meter_type === t} onChange={() => setMeter(i, { meter_type: t })} />
                        {t === 'intelligent' ? 'Compteur intelligent' : 'Compteur mécanique'}
                      </label>
                    ))}
                    {errors[eanKey] && <span className="text-[11px] text-red-600">{errors[eanKey]}</span>}
                  </div>
                </div>
              );
            })}
          </section>

          {sepaEligible && (
            <section className="space-y-3">
              {sectionTitle('Compte bancaire et domiciliation')}
              <div className="grid grid-cols-2 gap-3">
                <Field label={label('iban')} required={required.has('iban')} missing={missing.has('iban')} error={errors.iban} className="col-span-2">
                  <input value={valueOf('iban')} onChange={e => setIban(e.target.value)} placeholder="BE68 5390 0754 7034" disabled={accountLocked}
                    className={`${inputClass} font-mono text-[13px] ${borderFor(missing.has('iban'), errors.iban)} disabled:bg-neutral-50 disabled:text-neutral-500`} />
                </Field>
                <Field label={label('account_holder')} required={required.has('account_holder')} missing={missing.has('account_holder')}>
                  <input value={valueOf('account_holder')} onChange={e => set('account_holder', e.target.value || null)} disabled={accountLocked}
                    className={`${inputClass} ${borderFor(missing.has('account_holder'))} disabled:bg-neutral-50 disabled:text-neutral-500`} />
                </Field>
                <Field label={label('bic')} required={required.has('bic')} missing={missing.has('bic')} error={errors.bic}>
                  <input value={valueOf('bic')} onChange={e => set('bic', e.target.value || null)} placeholder="GEBABEBB" disabled={accountLocked}
                    className={`${inputClass} font-mono text-[13px] ${borderFor(missing.has('bic'), errors.bic)} disabled:bg-neutral-50 disabled:text-neutral-500`} />
                </Field>
              </div>
              <label className="flex items-center gap-2 text-xs text-neutral-700 cursor-pointer">
                <input type="checkbox" checked={!!draft.sepa_consent} onChange={e => set('sepa_consent', e.target.checked)} />
                Le membre accepte le paiement de ses factures par domiciliation SEPA
              </label>
              {accountLocked
                ? <p className="text-[11px] text-neutral-500">Mandat actif : le compte n’est plus modifiable. Pour changer de compte, révoquez le mandat puis envoyez-en un nouveau.</p>
                : <p className="text-[11px] text-neutral-500">BIC proposé automatiquement pour un IBAN belge. Créancier : Sun Is Up ASBL.</p>}
              {!isNew && contact && (
                <MandateSection contact={current()} operations={operations} dirty={dirty} onPatched={patchAndNotify} />
              )}
            </section>
          )}

          {billed && (
            <section className="space-y-3">
              {sectionTitle('Facturation')}
              <Field label="Canal de facturation" error={errors.billing_channel}>
                <select value={draft.billing_channel ?? ''} onChange={e => set('billing_channel', (e.target.value || null) as BillingChannel | null)}
                  className={`${inputClass} ${borderFor(false, errors.billing_channel)}`}>
                  <option value="">Automatique : {BILLING_CHANNEL_LABELS[autoChannel]}</option>
                  {BILLING_CHANNELS.map(ch => (
                    <option key={ch} value={ch} disabled={ch === 'direct_debit' && !!directDebitBlock && draft.billing_channel !== ch}>
                      {BILLING_CHANNEL_LABELS[ch]}{ch === 'direct_debit' && directDebitBlock ? ` (${directDebitBlock})` : ''}
                    </option>
                  ))}
                </select>
              </Field>
              <p className="text-[11px] text-neutral-500">
                {CHANNEL_NOTES[effectiveChannel]} Automatique : domiciliation si mandat actif (particulier consommateur), Accountable pour une société, e-mail sinon. L’e-mail au membre se choisit à chaque envoi (par défaut : nouveaux membres ; domiciliation : toujours).
              </p>
            </section>
          )}

          {estimateRelevant && (
            <section className="space-y-3">
              {sectionTitle('Estimation des économies')}
              <label className="flex items-center gap-2 text-xs text-neutral-700 cursor-pointer">
                <input type="checkbox" checked={!!draft.estimate_requested} onChange={e => set('estimate_requested', e.target.checked)} />
                Estimation demandée : envoyée avec le contrat
              </label>
              <div className="grid grid-cols-3 gap-3">
                <Field label={label('housing_type')}>
                  <select value={valueOf('housing_type')} onChange={e => set('housing_type', e.target.value || null)} className={`${inputClass} border-neutral-200`}>
                    <option value="">—</option>
                    <option value="maison">Maison</option>
                    <option value="appartement">Appartement</option>
                    <option value="autre">Autre</option>
                  </select>
                </Field>
                <Field label={label('annual_consumption_kwh')} vars={varsOf('annual_consumption_kwh')} error={errors.annual_consumption_kwh}>
                  <input inputMode="decimal" value={valueOf('annual_consumption_kwh')} onChange={e => setNumber('annual_consumption_kwh', e.target.value)}
                    placeholder="4000" className={`${inputClass} ${borderFor(false, errors.annual_consumption_kwh)}`} />
                </Field>
                <Field label={label('annual_cost_eur')} error={errors.annual_cost_eur}>
                  <input inputMode="decimal" value={valueOf('annual_cost_eur')} onChange={e => setNumber('annual_cost_eur', e.target.value)}
                    placeholder="Facultatif" className={`${inputClass} ${borderFor(false, errors.annual_cost_eur)}`} />
                </Field>
              </div>
              {savingsSettings && (
                <p className="text-[11px] text-neutral-500">
                  {savingsPreview
                    ? <>Estimation : environ <b className="text-neutral-800">{formatEuros(savingsPreview.annualSavings)} par an</b>
                        {' '}({savingsPreview.priceSource === 'declared' ? 'prix déclaré' : 'prix par défaut'} {formatKwhPrice(savingsPreview.pricePerKwh)}, taux {Math.round(savingsSettings.savingsRate * 100)} %).</>
                    : 'Indiquez la consommation annuelle pour calculer l’estimation.'}
                  {!savingsSettings.enabled && ' Envoi désactivé dans Paramètres → Estimation des économies.'}
                </p>
              )}
            </section>
          )}

          {producerLike && (
            <section className="space-y-3">
              {sectionTitle('Producteur')}
              <div className="grid grid-cols-2 gap-3">
                <Field label={label('vat_regime')} required={required.has('vat_regime')} missing={missing.has('vat_regime')} className="col-span-2">
                  <select
                    value={valueOf('vat_regime')}
                    onChange={e => set('vat_regime', (e.target.value || null) as VatRegime | null)}
                    className={`${inputClass} ${borderFor(missing.has('vat_regime'))}`}
                  >
                    <option value="">Sélectionner…</option>
                    {(Object.keys(VAT_LABELS) as VatRegime[]).map(v => <option key={v} value={v}>{VAT_LABELS[v]}</option>)}
                  </select>
                  {draft.entity_type !== 'morale' && (
                    <p className="mt-1 text-[11px] text-neutral-500">La convention personne physique ne prévoit que « non assujetti » (décompte d’achat).</p>
                  )}
                </Field>
                {input('installed_power_kwc', { type: 'number', placeholder: 'ex. 9,6' })}
                <Field label={label('installation_right')} required={required.has('installation_right')} missing={missing.has('installation_right')}>
                  <select
                    value={valueOf('installation_right')}
                    onChange={e => set('installation_right', (e.target.value || null) as InstallationRight | null)}
                    className={`${inputClass} ${borderFor(missing.has('installation_right'))}`}
                  >
                    <option value="">Sélectionner…</option>
                    {(Object.keys(INSTALLATION_RIGHT_LABELS) as InstallationRight[]).map(v => <option key={v} value={v}>{INSTALLATION_RIGHT_LABELS[v]}</option>)}
                  </select>
                </Field>
                {input('iban', { placeholder: 'BE68 5390 0754 7034' })}
                {input('account_holder')}
                <Field label="Période de remboursement" className="col-span-2">
                  <select
                    value={draft.reimbursement_period ?? 'quarterly'}
                    onChange={e => set('reimbursement_period', e.target.value as ReimbursementPeriod)}
                    className={`${inputClass} border-neutral-200`}
                  >
                    {(Object.keys(PERIOD_LABELS) as ReimbursementPeriod[]).map(p => (
                      <option key={p} value={p}>{PERIOD_LABELS[p]}{p === 'quarterly' ? ' (par défaut)' : ''}</option>
                    ))}
                  </select>
                </Field>
              </div>
              <p className="text-[11px] text-neutral-500">Mensuelle : remboursement chaque mois dès réception des données. Trimestrielle et semestrielle : périodes fixes (janvier–mars…, janvier–juin…).</p>
            </section>
          )}

          <section className="space-y-3">
            {sectionTitle('Contrat')}
            <div className="grid grid-cols-2 gap-3">
              {input('price_cents_kwh', { type: 'number', placeholder: suggested.price !== null ? `défaut ${formatPrice(suggested.price)}` : 'ex. 8' })}
              {isProsumer && input('sell_price_cents_kwh', { type: 'number', placeholder: suggested.sell !== null ? `défaut ${formatPrice(suggested.sell)}` : 'ex. 6' })}
              {input('annual_fee', { type: 'number', placeholder: String(defaultAnnualFee(draft.entity_type)) })}
            </div>
            {groupPrice && existingGroup && groupPrice.price !== null && (
              <p className="text-[11px] text-neutral-500">
                Prix du groupe « {existingGroup.name} » : {formatPrice(groupPrice.price)} c€/kWh
                {isProsumer && groupPrice.sellPrice !== null ? `, vente ${formatPrice(groupPrice.sellPrice)} c€/kWh` : ''}.
                {groupPriceDiffers && (priceEditable
                  ? <button type="button" onClick={applyGroupPrice} className="ml-1 font-medium text-amber-700 underline hover:text-amber-800">Appliquer le prix du groupe</button>
                  : <span className="ml-1 text-amber-700">Différent du prix du contrat.</span>)}
              </p>
            )}
            {!isNew && (
              <p className="text-[11px] text-neutral-500">
                Envoyé le <span className="text-neutral-800">{formatDateFr(draft.contract_sent_at)}</span> · Signé le <span className="text-neutral-800">{formatDateFr(draft.contract_signed_at)}</span>
              </p>
            )}
            {!isNew && (
              <div className="flex flex-wrap gap-2">
                <button onClick={handlePreview} disabled={!!busy} className={secondaryBtn}>{busyIcon('preview', Download)}Aperçu du contrat PDF</button>
                {stage === 'lead' && (
                  <button onClick={handleQuestionnaire} disabled={!!busy} className={secondaryBtn}
                    title={draft.questionnaire_sent_at ? `Formulaire envoyé le ${formatDateFr(draft.questionnaire_sent_at)}` : undefined}>
                    {busyIcon('questionnaire', Send)}{draft.questionnaire_sent_at ? 'Renvoyer le formulaire d’adhésion' : 'Envoyer le formulaire d’adhésion'}
                  </button>
                )}
                {draft.signed_contract_path && (
                  <button
                    onClick={() => run('view', () => openStoredFile('crm-documents', String(draft.signed_contract_path)))}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-emerald-200 bg-emerald-50 text-xs text-emerald-700 hover:bg-emerald-100"
                  >
                    <FileText className="w-3.5 h-3.5" />Voir le contrat signé
                  </button>
                )}
                {STAGE_INDEX[stage] >= STAGE_INDEX.contract_signed && (
                  <label className={`${secondaryBtn} cursor-pointer`}>
                    {busyIcon('signed', Upload)}{draft.signed_contract_path ? 'Remplacer le PDF signé' : 'Ajouter le PDF signé'}
                    <input type="file" accept="application/pdf" className="hidden" onChange={e => { const f = e.target.files?.[0]; if (f) handleSignedUpload(f); e.target.value = ''; }} />
                  </label>
                )}
              </div>
            )}
          </section>

          {!isNew && STAGE_INDEX[stage] >= STAGE_INDEX.contract_signed && (
            <section className="space-y-3">
              {sectionTitle('Sibelga et partage')}
              {stage === 'sibelga' && (
                <div className="space-y-1.5">
                  <p className="text-[11px] font-medium text-neutral-600">Statut Sibelga <span className="font-normal text-neutral-400">(plusieurs choix possibles, enregistré au clic)</span></p>
                  <SibelgaStatusPicker value={draft.sibelga_status ?? []} onChange={setSibelgaStatus} disabled={busy === 'sibelga-status'} />
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                {input('contract_signed_at', { type: 'date' })}
                {input('sibelga_encoded_at', { type: 'date' })}
                <Field label={label('sharing_operation_id')} vars={varsOf('sharing_operation_id')} required={required.has('sharing_operation_id')} className="col-span-2">
                  <select value={valueOf('sharing_operation_id')} onChange={e => set('sharing_operation_id', e.target.value || null)} className={`${inputClass} border-neutral-200`}>
                    <option value="">Sélectionner…</option>
                    {operations.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
                  </select>
                </Field>
                {input('sharing_start_date', { type: 'date' })}
                {variablePrice ? (
                  <p className="text-[11px] text-neutral-500 bg-neutral-50 border border-neutral-200 rounded-lg p-2.5 self-end">
                    Prix indexé (formule A × X) : il se modifie dans le formulaire participant de la page admin.
                  </p>
                ) : input('sharing_price_cents_kwh', { type: 'number' })}
                {isProsumer && !variablePrice && input('sharing_sell_price_cents_kwh', { type: 'number' })}
                {stage === 'active' && (
                  <Field label="TVA appliquée">
                    <select
                      value={draft.sharing_vat_rate === null || draft.sharing_vat_rate === undefined ? '' : String(draft.sharing_vat_rate)}
                      onChange={e => set('sharing_vat_rate', e.target.value === '' ? null : Number(e.target.value))}
                      className={`${inputClass} border-neutral-200`}
                    >
                      <option value="">Non renseignée</option>
                      {VAT_RATE_OPTIONS.map(o => <option key={o.value} value={String(o.value)}>{o.label}</option>)}
                    </select>
                  </Field>
                )}
              </div>
              {syncWithParticipants && (
                <p className="text-[11px] text-neutral-500">
                  {participantsLoaded
                    ? 'Noms, adresses, société, e-mail de facturation, date d’entrée, prix, cotisation et TVA sont ceux des fiches participants : les modifications y sont enregistrées, comme avec le formulaire de la page admin.'
                    : 'Chargement des fiches participants…'}
                </p>
              )}
            </section>
          )}

          {!isNew && stage === 'active' && (
            <section className="space-y-3">
              {sectionTitle('Plateforme')}
              <PlatformSection
                contact={current()}
                account={account}
                operations={operations}
                dirty={dirty}
                onPatched={patchAndNotify}
                extraActions={metersWithoutParticipant > 0 && (
                  <button onClick={handleCreateParticipants} disabled={!!busy} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-neutral-900 text-white text-xs font-semibold hover:bg-neutral-700 disabled:opacity-50">
                    {busyIcon('participants', UserPlus)}Créer {metersWithoutParticipant} fiche{metersWithoutParticipant > 1 ? 's' : ''} participant
                  </button>
                )}
              />
            </section>
          )}

          <section className="space-y-2">
            {sectionTitle('Notes')}
            <textarea rows={3} value={valueOf('notes')} onChange={e => set('notes', e.target.value)} className={`${inputClass} border-neutral-200 resize-none`} placeholder="Notes internes" />
          </section>
        </div>

        {/* Pied */}
        <div className="px-6 py-4 border-t border-neutral-100 bg-neutral-50/60 flex items-center justify-between gap-3 shrink-0">
          {!isNew ? (
            <button onClick={handleArchive} disabled={!!busy} className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs text-neutral-500 hover:text-red-600 hover:bg-red-50 disabled:opacity-50">
              {draft.archived_at ? <ArchiveRestore className="w-3.5 h-3.5" /> : <Archive className="w-3.5 h-3.5" />}
              {draft.archived_at ? 'Restaurer' : 'Archiver'}
            </button>
          ) : <span />}
          <div className="flex gap-2">
            <button onClick={() => save()} disabled={saving || !dirty || !participantsLoaded} className="flex items-center gap-1.5 px-4 py-2 rounded-lg border border-neutral-200 bg-white text-sm font-medium text-neutral-700 hover:bg-neutral-100 disabled:opacity-40">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              {isNew ? 'Créer le lead' : 'Enregistrer'}
            </button>
            {next && !draft.archived_at && (
              <button onClick={goNext} disabled={saving} className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold text-neutral-900 bg-[#F8BD3D] hover:bg-[#E5A935] disabled:opacity-50">
                {STAGES.find(s => s.key === next)?.label}<ArrowRight className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
